package com.bikompanion.ui.home

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.displayCutout
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.union
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.BiasAlignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.hazeGlass
import dev.chrisbanes.haze.hazeSource
import com.bikompanion.domain.MapLayerMode
import com.bikompanion.ui.composables.LocalHiddenCutoutInsets
import com.bikompanion.ui.composables.defaultGlassStyle
import com.bikompanion.ui.composables.glassPlateAlpha
import com.bikompanion.ui.composables.safeDrawingInsets
import com.bikompanion.ui.icons.BikeRack
import com.bikompanion.ui.icons.MaterialBikeDock
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.strings.LocalStrings

internal val NavMaxWidth = 400.dp

// The rail's items are square-ish stacks of icon over caption, one per mode.
private val RailItemSize = 64.dp

/**
 * One layer above the map (which registers itself at the default 0f in `HomeScreen`), so the sheets
 * — which have no source of their own and therefore consume every source — refract the bar too.
 */
private const val NavBarHazeZIndex = 1f

// Fixed rather than wrapped: the sliding indicator is a sibling of the item row and needs a height
// to fill, and the two items are the same shape anyway. Sized like an iOS tab bar item — compact,
// icon over a small caption — rather than the taller Material one.
private val ItemHeight = 50.dp
private val IconSize = 24.dp

// What the capsule takes from the map's edge, before the system insets: its content, the 6dp of
// padding either side of it, and the 16dp margin either side of that. The bottom bar's height and
// the rail's width, for anything that wants to frame content in the space they leave.
private val NavContainerPadding = 6.dp * 2 + 16.dp * 2
internal val NavBarFootprint = ItemHeight + NavContainerPadding
internal val NavRailFootprint = RailItemSize + NavContainerPadding

// Material 3's level-3 elevation, what a floating toolbar rests at. Kept modest on purpose: the
// shadow of a translucent shape is drawn under the whole capsule, not just around it, so a deeper
// one would grey out the refraction it is meant to be lifting off the map.
private val NavElevation = 6.dp

private val IndicatorSpring = spring<Float>(
    dampingRatio = Spring.DampingRatioLowBouncy,
    stiffness = Spring.StiffnessMediumLow,
)

/**
 * A floating tab bar in the iOS mould — a fully-rounded glass capsule over the map, icon above a
 * small caption, and one capsule highlight that wraps the whole selected item and slides between
 * them — using Material colour roles and typography.
 *
 * Must be a sibling of the map's [hazeState] source content, never a child of it: a `hazeGlass` node
 * drawn inside the node that `hazeSource` captures ends up sampling itself, and the blur silently
 * renders as nothing.
 *
 * It is also a source in its own right at [NavBarHazeZIndex], which is what lets a bottom sheet
 * drawn over it show the bar through its glass instead of covering it flat.
 *
 * With [vertical] (wide windows, where the bottom edge is far from the thumb) it is a rail of the
 * same capsule instead: items stack top to bottom and the indicator slides vertically. The caller
 * positions it, e.g. at `CenterStart`.
 */
@Composable
internal fun HomeNavigationBar(
    selectedMode: MapLayerMode,
    onModeSelect: (MapLayerMode) -> Unit,
    hazeState: HazeState,
    modifier: Modifier = Modifier,
    vertical: Boolean = false,
) {
    // `surfaceContainer` throughout: the Material 3 Expressive container role for a navigation bar
    // and for a standard floating toolbar alike, which is what this is.
    val glassTint = MaterialTheme.colorScheme.primaryContainer
    val glassStyle = remember(glassTint) { defaultGlassStyle(glassTint, CircleShape) }
    val container = MaterialTheme.colorScheme.surfaceContainer
    // A plate over the glass so the bar reads as a surface on any map tile — the refraction alone
    // leaves it legible only over busy ground. Kept well under opaque, or it would cancel the
    // refraction it sits on.
    val plate = container.copy(alpha = glassPlateAlpha(0.45f))
    val modes = listOf(MapLayerMode.Stations, MapLayerMode.Parking)
    val selectedIndex = modes.indexOf(selectedMode).coerceAtLeast(0)
    // One indicator that travels, rather than one per item fading in and out — the movement is
    // what says the two layers are alternatives to each other.
    val indicatorBias by animateFloatAsState(
        targetValue = -1f + 2f * selectedIndex / (modes.size - 1),
        animationSpec = IndicatorSpring,
    )
    // `primaryContainer` rather than the `secondaryContainer` a Material navigation bar would use:
    // it matches the FABs sitting directly above this bar, so the two floating controls read as one
    // family. Held below opaque — a solid pill would punch a hole in the glass it sits on.
    val indicatorColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.6f)
    val indicatorBorderColor = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f)
    val shadowColor = MaterialTheme.colorScheme.scrim

    Box(
        modifier = modifier
            .then(
                if (vertical) {
                    // Side insets as well as the bottom: on a landscape phone the system bar and
                    // any cutout sit on the side the rail hugs.
                    Modifier.windowInsetsPadding(safeDrawingInsets()).padding(16.dp)
                } else {
                    Modifier
                        .windowInsetsPadding(
                            WindowInsets.navigationBars
                                .union(WindowInsets.displayCutout)
                                .union(LocalHiddenCutoutInsets.current)
                                .only(WindowInsetsSides.Horizontal + WindowInsetsSides.Bottom),
                        ).padding(16.dp)
                        .widthIn(max = NavMaxWidth)
                        .fillMaxWidth()
                },
            ).shadow(
                elevation = NavElevation,
                shape = CircleShape,
                // The clip below does the clipping; letting the shadow do it too would crop the
                // spread it is drawing outside the capsule.
                clip = false,
                ambientColor = shadowColor,
                spotColor = shadowColor,
            ).clip(CircleShape)
            // Registers the bar as a source of its own, one layer above the map, so the sheets
            // (which sit higher still) refract the bar as well as the map behind it. Placed before
            // `hazeGlass` in the chain so the bar's own glass counts as captured content, and so
            // that glass sees only `Behind` sources — everything below zIndex 1, i.e. the map.
            .hazeSource(hazeState, zIndex = NavBarHazeZIndex)
            .hazeGlass(
                input = HazeInput.Sources(hazeState),
                style = glassStyle,
            ).background(plate)
            .padding(6.dp),
    ) {
        Box(
            modifier = if (vertical) {
                Modifier.width(RailItemSize).height(RailItemSize * modes.size)
            } else {
                Modifier.fillMaxWidth().height(ItemHeight)
            },
        ) {
            Box(
                modifier = Modifier
                    .then(
                        if (vertical) {
                            Modifier
                                .fillMaxHeight(1f / modes.size)
                                .fillMaxWidth()
                                .align(BiasAlignment(horizontalBias = 0f, verticalBias = indicatorBias))
                                .padding(vertical = 2.dp)
                        } else {
                            Modifier
                                .fillMaxWidth(1f / modes.size)
                                .fillMaxHeight()
                                .align(BiasAlignment(horizontalBias = indicatorBias, verticalBias = 0f))
                                .padding(horizontal = 2.dp)
                        },
                    ).clip(CircleShape)
                    .background(indicatorColor)
                    // A hairline edge: at this alpha the fill alone loses its shape over a
                    // light map tile.
                    .border(1.dp, indicatorBorderColor, CircleShape),
            )
            val item: @Composable (MapLayerMode, Modifier) -> Unit = { mode, itemModifier ->
                HomeNavigationItem(
                    label = mode.label,
                    icon = mode.icon,
                    selected = mode == selectedMode,
                    onClick = { onModeSelect(mode) },
                    modifier = itemModifier,
                )
            }
            if (vertical) {
                Column(modifier = Modifier.fillMaxSize()) {
                    modes.forEach { mode -> item(mode, Modifier.weight(1f).fillMaxWidth()) }
                }
            } else {
                Row(modifier = Modifier.fillMaxSize()) {
                    modes.forEach { mode -> item(mode, Modifier.weight(1f).fillMaxHeight()) }
                }
            }
        }
    }
}

private val MapLayerMode.label: String
    @Composable
    get() = when (this) {
        MapLayerMode.Stations -> LocalStrings.current.layerStations
        MapLayerMode.Parking -> LocalStrings.current.layerParking
    }

private val MapLayerMode.icon: ImageVector
    get() = when (this) {
        MapLayerMode.Stations -> MaterialBikeDock
        MapLayerMode.Parking -> BikeRack
    }

@Composable
private fun HomeNavigationItem(
    label: String,
    icon: ImageVector,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Material 3 gives the selected icon and its label different roles — the icon sits on the
    // indicator, the label sits on the bar — and both unselected states share `onSurfaceVariant`.
    val iconColor by animateColorAsState(
        targetValue = if (selected) {
            MaterialTheme.colorScheme.onPrimaryContainer
        } else {
            MaterialTheme.colorScheme.onSurfaceVariant
        },
    )
    val labelColor by animateColorAsState(
        targetValue = if (selected) {
            MaterialTheme.colorScheme.onSurface
        } else {
            MaterialTheme.colorScheme.onSurfaceVariant
        },
    )
    // A small pop on the icon so the tap is acknowledged even before the indicator has arrived.
    val iconScale by animateFloatAsState(
        targetValue = if (selected) 1.08f else 1f,
        animationSpec = IndicatorSpring,
    )

    Column(
        modifier = modifier
            .clip(CircleShape)
            .clickable(
                onClick = onClick,
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
            ),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = iconColor,
            modifier = Modifier
                .size(IconSize)
                .graphicsLayer {
                    scaleX = iconScale
                    scaleY = iconScale
                },
        )
        Spacer(modifier = Modifier.height(3.dp))
        Text(
            text = label,
            color = labelColor,
            style = MaterialTheme.typography.labelSmall,
            textAlign = TextAlign.Center,
            lineHeight = 0.9.em,
            modifier = Modifier.padding(horizontal = 8.dp),
        )
    }
}

@Preview
@Composable
private fun HomeNavigationBarStationsPreview() {
    HomeNavigationBarPreview(selectedMode = MapLayerMode.Stations, vertical = false)
}

@Preview
@Composable
private fun HomeNavigationBarParkingPreview() {
    HomeNavigationBarPreview(selectedMode = MapLayerMode.Parking, vertical = false)
}

@Preview
@Composable
private fun HomeNavigationRailStationsPreview() {
    HomeNavigationBarPreview(selectedMode = MapLayerMode.Stations, vertical = true)
}

@Preview
@Composable
private fun HomeNavigationRailParkingPreview() {
    HomeNavigationBarPreview(selectedMode = MapLayerMode.Parking, vertical = true)
}

@Composable
private fun HomeNavigationBarPreview(
    selectedMode: MapLayerMode,
    vertical: Boolean,
) {
    GlassPreviewSurface { hazeState ->
        HomeNavigationBar(
            selectedMode = selectedMode,
            onModeSelect = {},
            hazeState = hazeState,
            vertical = vertical,
        )
    }
}
